import { useEffect, useRef } from 'react'

type OnChangeFn = (rect: DOMRect, el: HTMLElement) => void

export function useBoundingClientRect(onChange?: OnChangeFn) {
  const elRef = useRef<HTMLElement | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const update = () => {
    const el = elRef.current
    if (el == null) return
    if (typeof onChangeRef.current !== 'undefined') {
      const rect = el.getBoundingClientRect?.()
      onChangeRef.current(rect, el)
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', update)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  // Clean up ResizeObserver on unmount
  useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect()
    }
  }, [])

  const refCb = (el: HTMLElement | null) => {
    // Clean up previous observer if element changes
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect()
      resizeObserverRef.current = null
    }

    elRef.current = el

    if (el) {
      // Create ResizeObserver to detect when element gets its actual size
      resizeObserverRef.current = new ResizeObserver(() => {
        update()
      })
      resizeObserverRef.current.observe(el)
    }

    update()
  }

  return refCb
}
